import * as THREE from 'three';
import type { Chapter, ChapterContext } from './types';
import { LETTER } from '../data/letter';
import skyFrag from '../render/lens/sky.frag.glsl?raw';
import quadVert from '../render/lens/quad.vert.glsl?raw';
import {
  FIELD_X, FIELD_Y, FLARE_A, FOV,
  clickToTheta, makeTargets, nearestLens, quietSpot, stepHold,
  type HoldState, type LensWithRadius, type Vec2
} from '../render/lens/microlens';

const meta = LETTER.chapters.find((c) => c.id === 'light')!;

/** the shader has room for three lenses; the reader's mass is lens 0 and the other two stay inert (thetaE 0) */
const N_LENS = 3;
const N_TARGET = 24;
const THETA_E = 0.006;
/** the mass starts at least this far (radians) from every star */
const START_CLEAR = 3 * THETA_E;
/** above this magnification the star is starting to brighten (the hint says so); above FLARE_A it is flaring */
const NEAR_A = 1.1;
/** extra brightness per unit of magnification above 1 (capped at 4 units), so a flaring star reaches 2 to 4 and over */
const FLARE_GAIN = 0.3;
const RING_SECONDS = 2.5;
/** one arrow-key press, radians: a little under the radius of a star's flare zone (0.82 thetaE), so a star can always be reached */
const KEY_STEP = 0.004;
/** how long "Show me" takes to carry the mass onto the nearest star */
const DEMO_SECONDS = 2.5;
const TARGET_SEED = 20260;

const HINT_NEAR = 'Closer. The star is starting to brighten.';
const HINT_HOLD = 'Hold it there.';
const HINT_DEMO = 'Watch the star as the mass passes over it.';

const BLUE_WHITE = new THREE.Vector3(0.74, 0.84, 1.0);
const GOLD = new THREE.Vector3(1.0, 0.8, 0.5);

/** "Show me": the mass glides from where it was to a star */
interface Glide { fromX: number; fromY: number; toX: number; toY: number; t: number }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const smooth = (v: number) => v * v * (3 - 2 * v);

/**
 * Chapter 4, "Light": gravitational microlensing. An invisible mass sits in a field of bulge stars and the reader drags it.
 * Put it on a star and hold it there: the star splits into two images and flares, and the discovery lands.
 * There is no clock and nothing to miss.
 */
export class LensChapter implements Chapter {
  readonly id = 'light';
  readonly title = 'Light';
  readonly description = 'A field of stars toward the centre of the galaxy; an invisible mass you drag bends their light, splitting and brightening any star it crosses.';
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(50, 16 / 9, 0.1, 10);

  private readonly material: THREE.ShaderMaterial;
  private readonly targets: Vec2[];
  private readonly baseColor: THREE.Vector3[] = [];
  private readonly targetColor: THREE.Vector3[] = [];
  /** the reader's mass, in sky angle (radians, y up) */
  private readonly lens: LensWithRadius = { x: 0, y: 0, vx: 0, vy: 0, thetaE: THETA_E };
  /** the lens as nearestLens wants it: a list of one */
  private readonly lensView: LensWithRadius[] = [this.lens];
  /** where the mass starts: clear of every star */
  private readonly start: Vec2;
  private readonly hold: HoldState = { target: -1, t: 0 };
  private readonly offset: Vec2 = { x: 0, y: 0 };

  private ctx: ChapterContext | null = null;
  private time = 0;
  private caught = false;
  private ringAge = Infinity;
  private glide: Glide | null = null;
  private hintText = '';
  private aspect = 16 / 9;

  constructor() {
    const field = makeTargets(TARGET_SEED);
    this.targets = field.pos;
    this.start = quietSpot(field.pos, START_CLEAR);
    const targetPos: THREE.Vector2[] = [];
    const targetColor: THREE.Vector3[] = [];
    for (let i = 0; i < N_TARGET; i++) {
      targetPos.push(new THREE.Vector2(field.pos[i].x, field.pos[i].y));
      const c = new THREE.Vector3().lerpVectors(BLUE_WHITE, GOLD, field.tint[i]).multiplyScalar(field.peak[i]);
      this.baseColor.push(c);
      targetColor.push(c.clone());
    }
    this.targetColor = targetColor;
    const thetaE = new Array<number>(N_LENS).fill(0);
    thetaE[0] = THETA_E;
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        res: { value: new THREE.Vector2(1, 1) },
        fov: { value: FOV },
        offset: { value: new THREE.Vector2() },
        time: { value: 0 },
        lenses: { value: Array.from({ length: N_LENS }, () => new THREE.Vector2()) },
        thetaE: { value: thetaE },
        targets: { value: targetPos },
        targetColor: { value: targetColor },
        ringPos: { value: new THREE.Vector2() },
        ringOn: { value: 0 },
        ringR: { value: THETA_E },
        cursorPos: { value: new THREE.Vector2() },
        cursorR: { value: THETA_E },
        cursorOn: { value: 1 }
      },
      vertexShader: quadVert,
      fragmentShader: skyFrag,
      depthTest: false,
      depthWrite: false
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    this.scene.add(quad);
    this.resetState();
  }

  enter(ctx: ChapterContext): void {
    this.ctx = ctx;
    this.resetState();
    ctx.say(meta.intro);
    ctx.hud.setLever({ kind: 'buttons', note: 'Drag anywhere to move the mass', items: [] });
    ctx.hud.setMove(meta.lever);
    ctx.hud.setHint('');
    this.showReadouts();
  }

  update(dt: number, t: number, width: number, height: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const step = clamp(dt, 0, 0.1);
    this.time += step;

    const aspect = width / Math.max(1, height);
    if (Math.abs(aspect - this.aspect) > 1e-4) { this.aspect = aspect; this.camera.aspect = aspect; this.camera.updateProjectionMatrix(); }

    // the field breathes a little, so a still picture still feels alive (the mass and the stars share the sky, so nothing slips)
    this.offset.x = 0.004 * Math.sin(this.time * 0.13);
    this.offset.y = 0.0025 * Math.cos(this.time * 0.09) - (height > width * 1.05 ? 0.07 : 0);   // upright phone: the stars ride above the captions

    this.stepGlide(step);

    // magnification of every star by the mass, and the star it is on top of (or closest to)
    let best = -1, bestA = 1;
    for (let i = 0; i < N_TARGET; i++) {
      const r = nearestLens(this.targets[i], this.lensView);
      // the picture already splits and stretches the star; this lifts it toward 2 to 4 so the bloom takes it
      this.targetColor[i].copy(this.baseColor[i]).multiplyScalar(1 + FLARE_GAIN * Math.min(r.A - 1, 4));
      if (r.A > bestA) { bestA = r.A; best = i; }
    }

    if (!this.caught) {
      this.showHint(bestA);
      if (stepHold(this.hold, best, bestA, step)) this.catchFlare(ctx);
    }

    this.updateRing(step);

    const u = this.material.uniforms;
    (u.res.value as THREE.Vector2).set(width, height);
    (u.offset.value as THREE.Vector2).set(this.offset.x, this.offset.y);
    u.time.value = t;
    this.syncLens();

    ctx.clock.advance(dt * 3600, dt * 3600);
    ctx.audio.setScene({ rate: 1, dist: 60, phase: 'light', orbitPhase: 0 });
  }

  exit(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.hud.setLever(null);
    ctx.hud.setMove(null);
    ctx.hud.setHint('');
  }

  /** Drag anywhere: the mass moves by the same amount on screen as the pointer. */
  onDrag(dx: number, dy: number): void {
    if (!this.ctx || this.caught || this.glide) return;
    const k = FOV / (this.ctx.canvas.clientWidth || 1);
    this.moveLens(this.lens.x + dx * k, this.lens.y - dy * k, 0.008, 0.35);
  }

  onWheel(_deltaY: number): void { /* the field of view is fixed */ }

  /** A click (or a tap) puts the mass right there, so a touch user can tap a star directly. */
  onClick(x: number, y: number): void {
    const ctx = this.ctx;
    if (!ctx || this.caught || this.glide) return;
    const th = clickToTheta(x, y, ctx.canvas.clientWidth, ctx.canvas.clientHeight, FOV, this.offset);
    this.moveLens(th.x, th.y, 0.014, 1);
  }

  /** Arrow keys nudge the mass. Returns true when a key was used. */
  onKey(key: string): boolean {
    if (!this.ctx || this.caught || this.glide) return false;
    let dx = 0, dy = 0;
    if (key === 'ArrowLeft') dx = -KEY_STEP;
    else if (key === 'ArrowRight') dx = KEY_STEP;
    else if (key === 'ArrowUp') dy = KEY_STEP;
    else if (key === 'ArrowDown') dy = -KEY_STEP;
    else return false;
    this.moveLens(this.lens.x + dx, this.lens.y + dy, 0.006, 0.5);
    return true;
  }

  /** The accessible route ("Show me"): glide the mass onto the nearest star; holding there lands the discovery as usual. */
  demonstrate(): void {
    const ctx = this.ctx;
    if (!ctx || this.caught || this.glide) return;
    let to = this.targets[0], bestD = Infinity;
    for (const T of this.targets) {
      const d = Math.hypot(T.x - this.lens.x, T.y - this.lens.y);
      if (d < bestD) { bestD = d; to = T; }
    }
    this.glide = { fromX: this.lens.x, fromY: this.lens.y, toX: to.x, toY: to.y, t: 0 };
    this.setHint(HINT_DEMO);
  }

  // ---------------------------------------------------------------------------------------------

  private resetState(): void {
    this.time = 0;
    this.caught = false;
    this.ringAge = Infinity;
    this.glide = null;
    this.hintText = '';
    this.hold.target = -1; this.hold.t = 0;
    this.offset.x = 0; this.offset.y = 0;
    this.lens.x = this.start.x; this.lens.y = this.start.y; this.lens.thetaE = THETA_E;
    for (let i = 0; i < N_TARGET; i++) this.targetColor[i].copy(this.baseColor[i]);
    const u = this.material.uniforms;
    u.ringOn.value = 0;
    u.cursorOn.value = 1;
    (u.offset.value as THREE.Vector2).set(0, 0);
    this.syncLens();
  }

  /** Where the mass may go: the field, but never past the edge of a wide screen. */
  private bounds(): Vec2 {
    const c = this.ctx?.canvas;
    const visibleY = c && c.clientWidth > 0 ? 0.5 * FOV * (c.clientHeight / c.clientWidth) : FIELD_Y;
    return { x: FIELD_X, y: Math.min(FIELD_Y, visibleY) };
  }

  /**
   * Put the mass at (x, y) in the field. With `snap`, a star closer than that is a magnet: the mass is pulled
   * `pull` of the way onto it, so a fingertip tap or a coarse drag still lands the flare on a phone.
   */
  private moveLens(x: number, y: number, snap = 0, pull = 0): void {
    const b = this.bounds();
    x = clamp(x, -b.x, b.x); y = clamp(y, -b.y, b.y);
    if (snap > 0 && pull > 0) {
      let best: Vec2 | null = null, bd = snap;
      for (const T of this.targets) { const d = Math.hypot(T.x - x, T.y - y); if (d < bd) { bd = d; best = T; } }
      if (best) { x += (best.x - x) * pull; y += (best.y - y) * pull; }
    }
    this.lens.x = x; this.lens.y = y;
  }

  /** Carry the mass along its glide, easing in and out. */
  private stepGlide(step: number): void {
    const g = this.glide;
    if (!g) return;
    g.t += step;
    const k = smooth(clamp(g.t / DEMO_SECONDS, 0, 1));
    this.lens.x = g.fromX + (g.toX - g.fromX) * k;
    this.lens.y = g.fromY + (g.toY - g.fromY) * k;
    if (g.t >= DEMO_SECONDS) this.glide = null;
  }

  /** Quiet feedback as the mass nears a star. The text is only touched when it changes. */
  private showHint(A: number): void {
    this.setHint(A > FLARE_A ? HINT_HOLD : A > NEAR_A ? HINT_NEAR : this.glide ? HINT_DEMO : '');
  }

  private setHint(text: string): void {
    if (text === this.hintText) return;
    this.hintText = text;
    this.ctx?.hud.setHint(text);
  }

  /** The mass goes into the shader, and the dashed ring that shows where it is. */
  private syncLens(): void {
    const u = this.material.uniforms;
    (u.lenses.value as THREE.Vector2[])[0].set(this.lens.x, this.lens.y);
    (u.thetaE.value as number[])[0] = this.lens.thetaE;
    (u.cursorPos.value as THREE.Vector2).set(this.lens.x, this.lens.y);
    u.cursorR.value = this.lens.thetaE;
    u.cursorOn.value = this.caught ? 0 : 1;
  }

  /** The discovery: the mass stays where it is, the Einstein ring closes on it, the letter answers. */
  private catchFlare(ctx: ChapterContext): void {
    this.caught = true;
    this.glide = null;
    this.ringAge = 0;
    ctx.say(meta.payoff);
    ctx.measure('light', LETTER.measurements.light);
    ctx.audio.blip('ok');
    ctx.hud.setLever(null);
    ctx.hud.setMove(null);
    this.hintText = '';
    ctx.hud.setHint('');
    this.showReadouts();
    ctx.finish();
  }

  /** The Einstein ring of the mass: closes onto the ring radius, holds, and fades out over RING_SECONDS. */
  private updateRing(step: number): void {
    const u = this.material.uniforms;
    if (this.ringAge >= RING_SECONDS) { u.ringOn.value = 0; return; }
    this.ringAge += step;
    if (this.ringAge >= RING_SECONDS) { u.ringOn.value = 0; return; }
    (u.ringPos.value as THREE.Vector2).set(this.lens.x, this.lens.y);
    u.ringR.value = this.lens.thetaE * (1 + 1.2 * Math.exp(-this.ringAge * 6));
    const fade = clamp((RING_SECONDS - this.ringAge) / 0.6, 0, 1);
    u.ringOn.value = 0.5 + 0.5 * fade;
  }

  private showReadouts(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const rows: [string, string][] = [
      ['The mass you are dragging', 'invisible, about 0.4 Suns'],
      ['Stars in the field', String(N_TARGET)]
    ];
    if (this.caught) {
      rows.push(['What happened', 'the star split in two and brightened']);
      rows.push(['Einstein radius here', 'about 0.3 arcseconds, drawn larger']);
    }
    ctx.hud.setReadouts(rows);
  }
}
